import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";

import * as print from "@arcgis/core/rest/print";
import PrintParameters from "@arcgis/core/rest/support/PrintParameters";
import PrintTemplate from "@arcgis/core/rest/support/PrintTemplate";
import MapView from "@arcgis/core/views/MapView";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { removeMask } from "../../../esri/widgets/print";
import {
  setPrintWidgetFinish,
  setPrintWidgetLoading,
  setPrintWidgetStep,
} from "../../../redux/action/PrintWidget-action";
import { view } from "../../../utils/API";
import {
  applyZoomLevelFilter,
  getOpsColor,
  removeZoomLevelFilter,
  ROLE_EDITOR,
} from "../../../utils/helper";
import { getLayerSymbology } from "../../../utils/symbologies";
import CustomSwiper, { CustomSwiperSlide } from "../../CustomSwiper";

import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import { LeftArrowIcon, MdClose, RightArrowIcon } from "../../Icons";
import {
  StyledArrowButton,
  StyledSubmitButton,
} from "../../Report/new/Footer/Footer-styled";
import ProgressBar from "../../Report/new/ProgressBar/ProgressBar";
import {
  ProgressLoader,
  StyledProgressBar,
} from "../../Report/new/ProgressBar/ProgressBar-styled";
import { useSnackbar } from "../../SnackBar";
import ConfigureMap from "../ExportPages/ConfigureMap";
import CustomizeMapDetails from "../ExportPages/CustomizeMapDetails";
import DisclaimerConfigurations from "../ExportPages/DisclaimerConfigurations";
import DocumentSettings from "../ExportPages/DocumentSettings";
import Drawings from "../ExportPages/Drawings";
import FinaliseExport from "../ExportPages/FinaliseExport";
import { addElementsToPDF } from "../helpers";
import {
  StyledBody,
  StyledHeader,
  StyledLoaderWrapper,
  StyledSidebarWrapper,
} from "./SidebarMenu-styled";

export const stepsValues = {
  1: 1,
  2: 2.7,
  3: 4,
};

export const transition = {
  1: 1,
  2: 5,
  3: 1.5,
};

const SidebarMenu = ({
  config,
  mapData,
  onMapDataChange,
  page,
  setPage,
  setDpi,
  dpi,
  setZoomMap,
  getPrintableData,
  zoomMap,
  zoomOut,
  setZoomOut,
  setDrawings,
  drawings,
  // setAddPublicCustomizations,
  addPublicCustomizations,
  setSymbologyCombinations,
}) => {
  const { t } = useTranslation("common");
  const abortControllers = useRef([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [layersWithLabels, setLayersWithLabels] = useState([]);
  const [layersWithCaptionsVisible, setLayersWithCaptionsVisible] = useState(
    []
  );
  const [layersVisibilityState, setLayersVisibilityState] = useState({});

  const [isPending, setTransition] = useTransition();
  const { step, isLoading, printOption, open } = useSelector(
    (state) => state.printWidget
  );
  const activeModule = useSelector((state) => state.activeModule);

  useEffect(() => {
    return () => {
      abortControllers.current.forEach((c) => {
        c.abort();
      });
    };
  }, []);

  const [openSnackbar] = useSnackbar({
    position: "top-center",
  });
  const swiperRef = useRef();
  const zoomInView = useRef();
  const zoomOutView = useRef();
  const allLayersWithVisibleLabels = useRef(new Set());
  const allLayersWithVisibleCaptions = useRef(new Set());

  const dispatch = useDispatch();

  useEffect(() => {
    const layersVisibility = {};
    const layersId = [];
    const captionVisibleLayersId = [];
    view.map.layers.forEach((layer) => {
      layersVisibility[layer.id] = layer.visible;
      if (
        layer.labelsVisible &&
        layer.layerConfig &&
        layer.layerConfig.labelField
      ) {
        if (layer.visible) {
          layersId.push(layer.id);
        }
        allLayersWithVisibleLabels.current.add(layer.id);
      }

      if (layer.layerConfigId && layer.isCommentBox) {
        if (layer.visible) {
          captionVisibleLayersId.push(layer.id);
        }
        allLayersWithVisibleCaptions.current.add(layer.id);
      }
    });

    let labelsHandler = null;
    let captionsHandler = null;
    view.map.layers.forEach((layer) => {
      if (allLayersWithVisibleLabels.current.has(layer.id)) {
        labelsHandler = reactiveUtils.watch(
          () => layer.visible,
          (visible) => {
            if (visible) {
              setLayersWithLabels((prev) => prev.concat(layer.id));
            } else {
              setLayersWithLabels((prev) =>
                prev.filter((id) => id !== layer.id)
              );
            }
          }
        );
      }

      if (allLayersWithVisibleCaptions.current.has(layer.id)) {
        captionsHandler = reactiveUtils.watch(
          () => layer.visible,
          (visible) => {
            if (visible) {
              setLayersWithCaptionsVisible((prev) => prev.concat(layer.id));
            } else {
              setLayersWithCaptionsVisible((prev) =>
                prev.filter((id) => id !== layer.id)
              );
            }
          }
        );
      }
    });

    setLayersVisibilityState(layersVisibility);
    setLayersWithLabels(layersId);
    setLayersWithCaptionsVisible(captionVisibleLayersId);

    return () => {
      labelsHandler?.remove();
      captionsHandler?.remove();
    };
  }, []);

  const handleStepChange = useCallback(
    (step) => {
      dispatch(setPrintWidgetStep(step));
    },
    [dispatch]
  );

  const setLoading = useCallback((v) => {
    dispatch(setPrintWidgetLoading(v));
  }, []);

  const removeZoomListeners = useCallback(() => {
    if (zoomInView.current && Array.isArray(zoomInView.current.listeners)) {
      zoomInView.current.listeners.forEach((l) => {
        l.remove();
      });
    }
  }, []);

  const assignSymbologyCombinations = useCallback(async () => {
    try {
      const symbologyCombinations = {};
      for (const layer of view.map.layers) {
        if (layer.renderer) {
          const layerView = await view.whenLayerView(layer);

          if (layerView.layer.visible && layerView.createQuery) {
            const query = layerView.createQuery();
            const res = await layerView.queryFeatures(query);
            const existingValues = {};

            if (res.features.length > 0) {
              const iconDefinition = layer.renderer.getIconDefinitions();
              const colorDefinition = layer.renderer.getColorDefinitions();
              const iconFields = [];
              const colorFields = [];

              if (iconDefinition.length > 0) {
                const { field } = iconDefinition[0];

                field.map((f) => iconFields.push(f.name));
              }

              if (colorDefinition.length > 0) {
                const { field } = colorDefinition[0];

                field.map((f) => colorFields.push(f.name));
              }

              // let fields = [...iconFields, ...colorFields];
              const available = new Set();
              res.features.forEach((f) => {
                const attributes = f.attributes;
                const iconFieldValues = [];
                const colorFieldValues = [];
                iconFields.forEach((iconField) => {
                  const value =
                    attributes[iconField] == null
                      ? "NULL"
                      : attributes[iconField];
                  iconFieldValues.push(value);
                });

                colorFields.forEach((colorField) => {
                  const value =
                    attributes[colorField] == null
                      ? "NULL"
                      : attributes[colorField];
                  colorFieldValues.push(value);
                });

                const combinedValues = [
                  ...iconFieldValues,
                  ...colorFieldValues,
                ].filter(Boolean);
                if (combinedValues.length > 0) {
                  available.add(combinedValues.join(","));
                }
              });

              symbologyCombinations[layer.id] = available;
            }
          }
        }
      }

      setSymbologyCombinations(symbologyCombinations);
    } catch (error) {
      console.error("Error assigning symbology combinations:", error);
    }
  }, []);

  let timer;
  const onDownload = async () => {
    removeZoomListeners();
    handleStepChange(1);
    const viewDiv = document.querySelector("#viewDiv");
    try {
      const preview = document.querySelector("#pdfMap");
      if (!preview) return;
      const previewWidth = preview.getBoundingClientRect().width;
      const previewHeight = preview.getBoundingClientRect().height;

      const dims = { width: previewWidth, height: previewHeight };

      const printableData = await getPrintableData();

      const promises = [];
      const { zoom } = printableData;

      if (viewDiv) {
        viewDiv.style.pointerEvents = "none";
      }
      await assignSymbologyCombinations();

      if (zoom.selected) {
        promises.push({
          key: "zoom",
          promise: fetchZoomMap({
            PrintTemplate,
            PrintParameters,
            print,
            view: zoomInView.current,
            size: zoom.size,
            orientation: zoom.orientation,
          }),
        });

        const pos = preview.getBoundingClientRect();
        const zoomDiff = view.zoom - zoomInView.current.zoom;

        if (Math.floor(zoomDiff) <= 0) {
          const zoomPoint = {
            x: zoomInView.current.extent.xmin,
            y: zoomInView.current.extent.ymax,
            spatialReference: {
              wkid: zoomInView.current.extent.spatialReference.wkid,
            },
          };

          const zoomPoint2 = {
            x: zoomInView.current.extent.xmax,
            y: zoomInView.current.extent.ymin,
            spatialReference: {
              wkid: zoomInView.current.extent.spatialReference.wkid,
            },
          };

          const zoomCoordinates = {
            x: view.toScreen(zoomPoint).x - pos.x,
            y:
              view.toScreen(zoomPoint).y -
              10 -
              (printableData.page.orientation === "Portrait" ? 55 : 0),
          };

          const helper = document.querySelector("#zoom-helper");
          if (helper) {
            helper.style.left = zoomCoordinates.x + "px";
            helper.style.top = zoomCoordinates.y + "px";
            helper.style.width =
              view.toScreen(zoomPoint2).x - view.toScreen(zoomPoint).x + "px";
            helper.style.height =
              view.toScreen(zoomPoint2).y - view.toScreen(zoomPoint).y + "px";
            helper.style.transform = "scale(1.4)";
            printableData.zoom.coordinates = {
              x: helper.getBoundingClientRect().x - pos.x,
              y:
                helper.getBoundingClientRect().y -
                10 -
                (printableData.page.orientation === "Portrait" ? 55 : 0),
              width: helper.getBoundingClientRect().width,
              height: helper.getBoundingClientRect().height,
            };
          }
        }
      }

      if (printableData.zoomOut.active) {
        promises.push({
          key: "zoomOut",
          promise: fetchZoomMap({
            PrintTemplate,
            PrintParameters,
            print,
            view: zoomOutView.current,
          }),
        });
      }

      view.map.layers.forEach((l) => {
        if (l.layerConfig && l.layerConfig.labelField) {
          l.labelingInfo.forEach((info) => {
            info.symbol.font.size = 7;
            info.symbol.haloSize = 1;
          });
        }
      });

      timer = setTimeout(() => {
        handleStepChange(2);
      }, 2000);

      if (viewDiv) {
        viewDiv.style.removeProperty("pointer-events");
      }

      //moved setting loading to request interceptor as we have to calculate view dimensions before sidebar and other widgets appear on screen
      const [main, ...rest] = await Promise.all([
        fetchMainMap({ PrintTemplate, PrintParameters, print, MapView }),
        ...promises.map((item) => item.promise),
      ]);

      clearTimeout(timer);
      view.map.layers.forEach((l) => {
        if (l.layerConfig && l.layerConfig.labelField) {
          l.labelingInfo.forEach((info) => {
            info.symbol.font.size = 9;
            info.symbol.haloSize = 2;
          });
        }

        applyZoomLevelFilter(l, config);
      });

      rest.map((item, index) => {
        printableData[promises[index].key].url = item.url;
      });

      handleStepChange(3);

      await addElementsToPDF(main.url, dims, printableData);
      openSnackbar(t("print.final.downloadComplete"), 15000);
    } catch (err) {
      console.log(err);

      if (err.name !== "AbortError") {
        if (err.message === "Timeout exceeded") {
          const email = t("screen.message.contactEmail");

          openSnackbar(t("screen.message.timeoutError", { 1: email }), 15000);
        } else if (err.message && err.name) {
          openSnackbar(err.message, 15000);
        } else if (err.messages && Array.isArray(err.messages)) {
          const errorMessages = err.messages.filter(
            (err) => err.type === "error"
          );
          const descriptionErrors = Array.from(
            new Set(errorMessages.map((message) => message.description))
          );
          openSnackbar(descriptionErrors.join(" "), 15000);
        } else {
          openSnackbar(t("screen.message.error"), 15000);
        }
      }
      if (viewDiv) {
        viewDiv.style.removeProperty("pointer-events");
      }

      view.map.layers.forEach((l) => {
        if (l.layerConfig && l.layerConfig.labelField) {
          l.labelingInfo.forEach((info) => {
            info.symbol.font.size = 9;
          });
        }
        applyZoomLevelFilter(l, config);
      });
    } finally {
      handleClose();
      setLoading(false);
      clearTimeout(timer);
    }
  };

  const fetchMainMap = async ({ PrintTemplate, PrintParameters, print }) => {
    const template = new PrintTemplate({
      format: "pdf",
      attributionVisible: false,
      exportOptions: {
        dpi: 300,
      },
      layout: "",
      layoutOptions: {
        titleText: "",
        authorText: mapData.mapAuthor,
        copyright: config?.mapCpr,
        title: mapData.title,
        legendLayers: [],
      },
    });

    const promises = [];

    const params = new PrintParameters({
      view: view,
      template: template,
    });

    let locationsLayer, locationsGraphicsLayer;
    const scaleMap = new Map();
    params.view.map.layers.forEach((layer) => {
      if (layer.id === "locationsGraphicsLayer") {
        locationsGraphicsLayer = layer;
      }

      if (
        layer.layerConfig?.alias === "locations_basemap" &&
        page.size === "A0"
      ) {
        locationsLayer = layer;
        locationsLayer.layers.map((l) => {
          const scale = l.minScale;
          scaleMap.set(l.id, scale);
          l.minScale = 0;
        });
        const printMask = document.querySelector("#print-mask");
        if (printMask) {
          // Convert the Map object to a serializable JSON string
          const scaleMapJson = JSON.stringify(Array.from(scaleMap.entries()));

          // Store the scale map as a data attribute on the print mask
          printMask.setAttribute("data-scale-map", scaleMapJson);
        }
      }

      const layerViewPromise = params.view
        .whenLayerView(layer)
        .then(async (layerView) => {
          // If layer is updating, create a promise that resolves when updating is done
          if (layerView.updating) {
            await new Promise((resolve) => {
              let timeout;
              if (
                !locationsLayer ||
                (locationsLayer && locationsLayer.id !== layer.id)
              ) {
                timeout = setTimeout(() => {
                  clearInterval(checkUpdating);
                  resolve();
                }, 5000); // 45 second timeout
              }

              const checkUpdating = setInterval(() => {
                if (!layerView.updating) {
                  clearInterval(checkUpdating);
                  clearTimeout(timeout);
                  resolve();
                }
              }, 500); // Check every 100ms
            });
          }
          return layerView;
        });

      promises.push(layerViewPromise);
    });

    const layerViews = await Promise.all(promises);

    layerViews.forEach((lv) => {
      const sizeMap = getLayerSymbology(lv.layer, config)?.sizeMap;
      if (!!sizeMap && sizeMap.zoom && lv.layer.visible) {
        removeZoomLevelFilter(lv);
      }
    });

    const controller = new AbortController();
    abortControllers.current.push(controller);

    const promise = print.execute(config.printServiceUrl, params, {
      signal: controller.signal,
      timeout: 0,
    });

    return await promise;
  };

  const fetchZoomMap = async ({
    PrintTemplate,
    PrintParameters,
    print,
    view,
    size,
    orientation,
  }) => {
    let layout = "";
    if (size && orientation) {
      const layoutSize = size.substring(0, 1).toUpperCase();
      const layoutOrientation =
        orientation.substring(0, 1).toUpperCase() + orientation.substring(1);
      layout = `LogIE_LogisticsCluster_${layoutSize}_${layoutOrientation}_Zoom_Map`;
    } else {
      layout = "LogIE_LogisticsCluster_Zoom_Map";
    }

    const template = new PrintTemplate({
      format: "pdf",
      attributionVisible: false,
      exportOptions: {
        dpi: 300,
      },
      layout: layout,
      layoutOptions: {
        titleText: "",
        authorText: "",
      },
    });

    const params = new PrintParameters({
      view: view,
      template: template,
    });
    const controller = new AbortController();
    abortControllers.current.push(controller);
    return await print.execute(config.printServiceUrl, params, {
      signal: controller.signal,
      timeout: 0,
    });
  };

  const returnExistingLabels = useCallback(() => {
    view.map.layers.forEach((l) => {
      if (l.id in layersVisibilityState) {
        l.visible = layersVisibilityState[l.id];
      }
      if (layersWithLabels.includes(l.id)) {
        l.labelsVisible = true;
      }

      if (layersWithCaptionsVisible.includes(l.id)) {
        l.visible = true;
      }
    });
  }, [layersWithLabels, layersWithCaptionsVisible, layersVisibilityState]);

  const handleClose = useCallback(() => {
    removeMask();
    dispatch(setPrintWidgetFinish());
    removeZoomListeners();
    returnExistingLabels();
  }, [dispatch, removeZoomListeners, returnExistingLabels]);

  const isEditor = useMemo(
    () =>
      config.role === ROLE_EDITOR || config.printMap.allowPublicPrintAsEditor,
    [config]
  );

  const handleDrawings = useCallback(
    (params) => {
      setTransition(() => {
        setDrawings((prev) => ({
          ...prev,
          ...params,
        }));
      });
    },
    [setDrawings]
  );

  const handleNext = useCallback(
    (customization) => {
      let activeIndex = swiperRef.current.swiper.activeIndex;
      const isCustom = customization || addPublicCustomizations;
      if (!isCustom && activeIndex === 2 && !isEditor) {
        swiperRef.current.swiper.slideTo(6);
      } else {
        swiperRef.current.swiper.slideNext();
      }

      activeIndex = swiperRef.current.swiper.activeIndex;
      setCurrentSlide(activeIndex);

      if (isEditor || isCustom) {
        const index = isEditor ? 2 : 3;
        if (activeIndex === index) {
          handleDrawings({ active: true });
        } else if (activeIndex > index && drawings.active) {
          handleDrawings({ viewModeEnabled: true });
        }
      }
    },
    [isEditor, handleDrawings, drawings, addPublicCustomizations]
  );

  const handlePrev = useCallback(() => {
    let activeIndex = swiperRef.current.swiper.activeIndex;
    if (!addPublicCustomizations && activeIndex === 6 && !isEditor) {
      swiperRef.current.swiper.slideTo(2);
    } else {
      swiperRef.current.swiper.slidePrev();
    }

    swiperRef.current.swiper.slidePrev();

    // if (addPublicCustomizations && activeIndex === 3 && !isEditor) {
    //   // setAddPublicCustomizations(false);
    // }

    activeIndex = swiperRef.current.swiper.activeIndex;
    setCurrentSlide(activeIndex);

    if (isEditor || addPublicCustomizations) {
      const index = isEditor ? 1 : 2;
      if (activeIndex === index && drawings.active) {
        handleDrawings({
          active: false,
          elements: drawings.api.getSceneElements(),
        });
      } else if (activeIndex === index + 1 && drawings.active) {
        handleDrawings({ viewModeEnabled: false });
      }
    }
  }, [drawings, isEditor, handleDrawings, addPublicCustomizations]);

  const opsColor = useMemo(
    () => getOpsColor(config, activeModule),
    [config, activeModule]
  );

  const slides = useMemo(() => {
    const allSlides = [
      {
        id: "documentSettings",
        title: t("print.documentSettings.title"),
        component: (
          <DocumentSettings
            config={config}
            dpi={dpi}
            setDpi={setDpi}
            page={page}
            setPage={setPage}
            t={t}
          />
        ),
      },
      {
        id: "setupMapView",
        title: t("print.setUpMapView.title"),
        component: (
          <ConfigureMap
            zoomViewRef={zoomInView}
            zoomMap={zoomMap}
            setZoomMap={setZoomMap}
            setZoomOut={setZoomOut}
            zoomOutViewRef={zoomOutView}
            config={config}
            zoomOut={zoomOut}
            t={t}
            layersWithLabels={layersWithLabels}
            layersWithCaptionsVisible={layersWithCaptionsVisible}
          />
        ),
      },
    ];

    if (isEditor) {
      allSlides.push(
        ...[
          {
            id: "drawings",
            title: t("print.drawings.title"),
            component: <Drawings t={t} drawings={drawings} config={config} />,
          },
          {
            id: "mapDetails",
            title: t("print.mapDetails.title"),
            component: (
              <CustomizeMapDetails
                mapData={mapData}
                config={config}
                isEditor={true}
                onMapDataChange={onMapDataChange}
                t={t}
                addCustomizations={addPublicCustomizations}
              />
            ),
          },
        ]
      );
    } else if (addPublicCustomizations) {
      allSlides.push(
        ...[
          {
            id: "drawings",
            title: t("print.drawings.title"),
            component: <Drawings t={t} drawings={drawings} config={config} />,
          },
          {
            id: "disclaimer",
            title: t("print.mapDetails.title"),
            component: (
              <StyledBody opsColor={opsColor}>
                <DisclaimerConfigurations
                  index={1}
                  t={t}
                  config={config}
                  isEditor={isEditor}
                  onMapDataChange={onMapDataChange}
                  mapData={mapData}
                />
              </StyledBody>
            ),
          },
        ]
      );
    }

    allSlides.push({
      id: "final",
      title: t("print.final.title"),
      component: (
        <FinaliseExport
          config={config}
          onDownload={onDownload}
          loading={isLoading}
          t={t}
        />
      ),
    });

    if (!isEditor && addPublicCustomizations) {
      return [
        ...allSlides.slice(0, 1),
        {
          id: "branding",
          title: t("print.branding.title"),
          component: (
            <CustomizeMapDetails
              mapData={mapData}
              config={config}
              isEditor={isEditor}
              onMapDataChange={onMapDataChange}
              t={t}
              addCustomizations={addPublicCustomizations}
            />
          ),
        },
        ...allSlides.slice(1),
      ];
    }

    return allSlides;
  }, [
    config,
    mapData,
    onMapDataChange,
    page,
    setPage,
    setDpi,
    dpi,
    setZoomMap,
    getPrintableData,
    zoomMap,
    setZoomOut,
    setDrawings,
    drawings,
    isLoading,
    isEditor,
    t,
    addPublicCustomizations,
    // setAddPublicCustomizations,
    handleNext,
    opsColor,
    layersWithLabels,
  ]);

  return (
    <StyledSidebarWrapper>
      <StyledHeader>
        <ProgressBar
          total={slides.length - 1}
          currentPosition={currentSlide}
          color={opsColor}
        />
        <p style={{ textWrap: "wrap", textAlign: "left" }}>
          {slides[currentSlide].title}
        </p>
      </StyledHeader>
      <StyledArrowButton
        radius={4}
        onClick={handleClose}
        style={{
          cursor: "pointer",
          minHeight: "auto",
          position: "absolute",
          top: "10px",
          right: "0px",
          zIndex: 2,
        }}
      >
        <MdClose fill="#ffffff" width="20px" height="20px" />
      </StyledArrowButton>
      <StyledLoaderWrapper loading={isLoading || step > 0} opsColor={opsColor}>
        {isLoading || step > 0 ? (
          <calcite-loader></calcite-loader>
        ) : (
          <div style={{ height: 192 }} />
        )}
        <div
          style={{
            width: "90%",
            height: 10,
            borderRadius: 10,
            overflow: "hidden",
            background: "#a8b1b780",
            marginTop: -30,
            position: "relative",
          }}
        >
          <ProgressLoader color={opsColor} />
          <StyledProgressBar
            style={{ transition: `all ${transition[step]}s` }}
            bgcolor={opsColor}
            progress={(100 / 4) * (stepsValues[step] ?? 0)}
          />
        </div>
        <p style={{ textAlign: "center" }}>
          {t("print.final.loadingSteps." + step)}
        </p>
      </StyledLoaderWrapper>
      <CustomSwiper
        ref={swiperRef}
        prevent-interaction-on-transition
        no-swiping
        allow-touch-move={false}
        prevent-clicks={false}
        simulate-touch={false}
        prevent-clicks-propagation={false}
        virtual="true"
      >
        {slides.map((slide, index) => (
          <CustomSwiperSlide key={`${slide.title}-${index}`} tabIndex={index}>
            {!isLoading &&
              step === 0 &&
              currentSlide === index &&
              slide.component}
          </CustomSwiperSlide>
        ))}
      </CustomSwiper>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          padding: "5px 10px",
          alignItems: "center",
        }}
      >
        <div
          style={{ borderRadius: "4px", overflow: "hidden", display: "flex" }}
        >
          <StyledArrowButton
            disable={currentSlide === 0}
            bg={opsColor ?? "#b21b0c"}
            style={{
              borderBottomLeftRadius: 6,
              borderTopLeftRadius: 6,
              overflow: "hidden",
            }}
            onClick={handlePrev}
          >
            <RightArrowIcon width={18} color="#FFFFFF" />
          </StyledArrowButton>
          <div
            style={{ width: "1px", height: "100%", background: "#F3F3F3" }}
          />
          <StyledArrowButton
            disable={currentSlide === slides.length - 1}
            bg={opsColor ?? "#b21b0c"}
            style={{
              borderBottomRightRadius: 6,
              borderTopRightRadius: 6,
              overflow: "hidden",
            }}
            onClick={() => handleNext()}
          >
            <LeftArrowIcon width={18} color="#FFFFFF" />
          </StyledArrowButton>
        </div>
        <StyledSubmitButton
          disable={currentSlide === slides.length - 1}
          onClick={() => handleNext()}
          bg={opsColor}
        >
          {t("screen.popup.actions.next")}
        </StyledSubmitButton>
      </div>
    </StyledSidebarWrapper>
  );
};

export default SidebarMenu;
